import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Button } from '@/shared/ui/buttons/Button';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage, extractFieldErrors } from '@/shared/api/errors';
import { apiAddressGet, apiAddressPut } from '@/entities/addresses';
import { FeaturesAddressesAddressForm } from '@/features/addresses';
import {
  addressFormSchema,
  emptyAddressFormValues,
  mapAddressToFormValues,
  mapFormValuesToPayload,
  type AddressFormValues,
} from '@/features/addresses/modules/types';
import type {
  ApiAddressesGetItem,
  ApiAddressesGetResponse,
  ApiAddressesPostBody,
} from '@/shared/types/api/addresses';

const FORM_ID = 'address-edit-form';
const BACK_TO = '/settings/addresses';

export const WidgetsAddressEditMain = () => {
  const { t } = useTranslation('addresses');
  const navigate = useNavigate();
  const toast = useToast();
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const cached = queryClient
    .getQueryData<ApiAddressesGetResponse>(queryKeys.addresses.all)
    ?.find((item) => item.id === id);

  const detail = useQuery<ApiAddressesGetItem, Error>({
    queryKey: queryKeys.addresses.detail(id),
    queryFn: () => apiAddressGet(id),
    enabled: Boolean(id),
    initialData: cached,
    staleTime: 60_000,
  });

  const form = useForm<AddressFormValues>({
    resolver: zodResolver(addressFormSchema),
    defaultValues: emptyAddressFormValues,
    mode: 'onBlur',
  });

  const address = detail.data;
  useEffect(() => {
    if (address) form.reset(mapAddressToFormValues(address));
    // reset once per loaded address; refetches return new refs and must not clobber edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address?.id]);

  const mutation = useMutate({
    api: (payload: ApiAddressesPostBody) => apiAddressPut(id, payload),
    invalidateKeys: [queryKeys.addresses.all, queryKeys.addresses.detail(id)],
    onSuccess: () => {
      toast.success(t('toast.updated'));
      navigate(BACK_TO);
    },
    onError: (err, message) => {
      const fieldErrors = extractFieldErrors(err);
      Object.entries(fieldErrors).forEach(([field, msg]) => {
        if (field in emptyAddressFormValues) {
          form.setError(field as keyof AddressFormValues, { type: 'server', message: msg });
        }
      });
      toast.error(message);
    },
  });

  const onSubmit = (values: AddressFormValues): void => {
    mutation.mutate(mapFormValuesToPayload(values));
  };

  return (
    <>
      <PageHeader title={t('pageTitle.edit')} showBack backTo={BACK_TO} />
      <div className="flex flex-col gap-4 p-4 md:mx-auto md:w-full md:max-w-2xl lg:p-6">
        {detail.isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : detail.isError ? (
          <ErrorState
            message={extractErrorMessage(detail.error)}
            onRetry={() => void detail.refetch()}
          />
        ) : (
          <>
            <FeaturesAddressesAddressForm id={FORM_ID} form={form} onSubmit={onSubmit} />
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(BACK_TO)}
                disabled={mutation.isPending}
                block
              >
                {t('common:cancel')}
              </Button>
              <Button type="submit" form={FORM_ID} loading={mutation.isPending} block>
                {t('common:save')}
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
};
